import { combine, createEffect, createEvent, createStore, sample } from "effector";
import { debounce, reset } from "patronum";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { appMounted, routePathNameChanged } from "@features/app/model";
import { levelUp as trackLevelUp, xpAwarded as trackXpAwarded } from "@features/shared/events";
import type { IMetadataContract } from "@features/student/metadata/model";
import type { ITaskLayoutUIContract } from "@features/tasks/ui/task-layout";
import type { ITasksContract } from "@features/tasks/model";
import type { ILessonCompletionContract } from "@features/lesson-completion/model";
import type { IUserAuthContract } from "@features/student/auth/model";
import type { IAmplitudeFlagsContract } from "@features/amplitude-flags/model";
import { XP_REWARDS, levelForXp, levelProgress, xpForCompletion, type XpBreakdown, type XpSource } from "./levels";

export type { XpSource, XpBreakdown } from "./levels";

export type XpAwardRequest = {
    source: XpSource;
    amount: number;
    /** Dedupe key for the current lesson visit (`task:<id>`, `quiz:<id>`, `lesson:<id>:<source>`). */
    key: string;
    /** Element marker (`data-xp-source`) the reward flies from; null = the lesson-done screen chip. */
    taskId?: string | null;
    breakdown?: XpBreakdown[];
    /** Set on lesson awards: repeat completions are remembered per lesson per day. */
    lessonId?: number | null;
};

export type XpAward = {
    id: number;
    source: XpSource;
    amount: number;
    totalBefore: number;
    totalAfter: number;
    taskId: string | null;
    key: string;
    breakdown: XpBreakdown[];
    lessonId: number | null;
};

export type LevelUp = { from: number; to: number };
export type XpFlight = { id: number; amount: number; source: XpSource; taskId: string | null };

export const XP_METADATA_KEY = "xp_total";
/** `YYYY-MM-DD:lessonId,lessonId`: the lessons whose repeat completion already paid today. */
export const XP_REPEAT_LOG_KEY = "xp_repeat_log";
const PERSIST_DEBOUNCE_MS = 1500;
const MAX_WRITE_RETRIES = 3;

let awardSequence = 0;

const parseServerXp = (metadata: Record<string, string>): number => {
    const value = Number.parseInt(metadata[XP_METADATA_KEY] ?? "", 10);
    return Number.isFinite(value) && value >= 0 ? value : 0;
};

type RepeatLog = { day: string; lessons: number[] };

/** Local calendar day, so the cap resets overnight for the student, not at UTC midnight. */
const todayKey = (now = new Date()) =>
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

export const parseRepeatLog = (metadata: Record<string, string>, now = new Date()): RepeatLog => {
    const [day = "", list = ""] = (metadata[XP_REPEAT_LOG_KEY] ?? "").split(":");
    if (day !== todayKey(now)) return { day: todayKey(now), lessons: [] };
    const lessons = list
        .split(",")
        .map((id) => Number.parseInt(id, 10))
        .filter((id) => Number.isFinite(id));
    return { day, lessons };
};

export const formatRepeatLog = (log: RepeatLog) => `${log.day}:${log.lessons.join(",")}`;

const repeatPaidToday = (log: RepeatLog, lessonId: number, now = new Date()) =>
    log.day === todayKey(now) && log.lessons.includes(lessonId);

const withRepeatPaid = (log: RepeatLog, lessonId: number, now = new Date()): RepeatLog => {
    const today = todayKey(now);
    const lessons = log.day === today ? log.lessons : [];
    return { day: today, lessons: lessons.includes(lessonId) ? lessons : [...lessons, lessonId] };
};

// Events
const xpAwardRequested = createEvent<XpAwardRequest>();
const xpAwarded = createEvent<XpAward>();
const xpFlightFinished = createEvent<number>();
const xpPillSynced = createEvent<number>();
const levelUpShown = createEvent();
const xpPersistRequested = createEvent();
const xpFlushRequested = createEvent();
const serverXpReceived = createEvent<number>();
const repeatLogReceived = createEvent<RepeatLog>();
const xpSynced = createEvent<{ base: number; delta: number }>();
const xpWriteStarted = createEvent();
const xpWriteSettled = createEvent<{ failed: boolean }>();

// Stores
const $xpEnabled = createStore(false);
const $xpServerBase = createStore<number | null>(null);
const $xpPendingDelta = createStore(0);
const $xpTotal = combine($xpServerBase, $xpPendingDelta, (base, delta) => (base ?? 0) + delta);
const $xpLevel = $xpTotal.map(levelForXp);
const $xpProgress = $xpTotal.map(levelProgress);
const $awardedKeys = createStore<Record<string, true>>({});
// Refreshing the lesson-done screen and finishing again used to pay the repeat reward every time.
const $repeatLog = createStore<RepeatLog>({ day: "", lessons: [] }).on(repeatLogReceived, (_, log) => log);
const $levelUp = createStore<LevelUp | null>(null);
// Display copies: the level-up screen and the lesson-done chip read these after the flow consumed the originals.
const $lastLevelUp = createStore<LevelUp | null>(null);
const $lastLessonAward = createStore<number | null>(null);
const $xpFlights = createStore<XpFlight[]>([]);
const $xpPillValue = createStore(0);
const $xpWriteInFlight = createStore(false);
const $xpWriteRetries = createStore(0);
const $persistDebounceMs = createStore(PERSIST_DEBOUNCE_MS);

// Effects
const trackXpAwardedFx = createEffect((award: XpAward) => {
    trackXpAwarded(award.source, award.amount, award.totalAfter, levelForXp(award.totalAfter), award.breakdown);
});
const trackLevelUpFx = createEffect(({ from, to, total }: LevelUp & { total: number }) => {
    trackLevelUp(from, to, total);
});

// Award pipeline: one award per key per lesson visit, only while the flag is on.
sample({
    clock: xpAwardRequested,
    source: { enabled: $xpEnabled, keys: $awardedKeys, total: $xpTotal },
    filter: ({ enabled, keys }, request) => enabled && request.amount > 0 && !keys[request.key],
    fn: ({ total }, request): XpAward => ({
        id: ++awardSequence,
        source: request.source,
        amount: request.amount,
        totalBefore: total,
        totalAfter: total + request.amount,
        taskId: request.taskId ?? null,
        key: request.key,
        breakdown: request.breakdown ?? [{ source: request.source, amount: request.amount }],
        lessonId: request.lessonId ?? null
    }),
    target: xpAwarded
});

$repeatLog.on(xpAwarded, (log, award) =>
    award.source === "lesson_repeat" && award.lessonId !== null ? withRepeatPaid(log, award.lessonId) : log
);

$awardedKeys.on(xpAwarded, (keys, award) => ({ ...keys, [award.key]: true })).reset(routePathNameChanged);
$xpPendingDelta.on(xpAwarded, (delta, award) => delta + award.amount);
$xpFlights
    .on(xpAwarded, (flights, award) => [
        ...flights,
        { id: award.id, amount: award.amount, source: award.source, taskId: award.taskId }
    ])
    .on(xpFlightFinished, (flights, id) => flights.filter((flight) => flight.id !== id));

sample({ clock: xpAwarded, target: [trackXpAwardedFx, xpPersistRequested] });

const levelRaised = sample({
    clock: xpAwarded,
    filter: (award) => levelForXp(award.totalAfter) > levelForXp(award.totalBefore)
});

// `from` survives several level-ups in one lesson; it is only cleared once the screen was shown.
sample({
    clock: levelRaised,
    source: $levelUp,
    fn: (previous, award): LevelUp => ({
        from: previous?.from ?? levelForXp(award.totalBefore),
        to: levelForXp(award.totalAfter)
    }),
    target: $levelUp
});
sample({
    clock: levelRaised,
    fn: (award) => ({ from: levelForXp(award.totalBefore), to: levelForXp(award.totalAfter), total: award.totalAfter }),
    target: trackLevelUpFx
});
$levelUp.reset(levelUpShown);
sample({ clock: $levelUp.updates, filter: (value): value is LevelUp => value !== null, target: $lastLevelUp });
$lastLessonAward
    .on(xpAwarded, (last, award) =>
        award.source === "lesson" || award.source === "lesson_repeat" ? award.amount : last
    )
    .reset(routePathNameChanged);

// The pill shows the total once the reward has landed on it (or straight away when nothing is flying).
sample({ clock: xpFlightFinished, source: $xpTotal, target: $xpPillValue });
sample({ clock: xpPillSynced, target: $xpPillValue });

// Reconciliation: the server value never lowers the total. Before the first load every award is a
// pending delta on top of whatever the server will report; afterwards delta = total - base.
sample({
    clock: serverXpReceived,
    source: { base: $xpServerBase, delta: $xpPendingDelta, total: $xpTotal },
    fn: ({ base, delta, total }, server) => {
        if (base === null) return { base: server, delta };
        const nextBase = Math.max(base, server);
        return { base: nextBase, delta: Math.max(0, total - nextBase) };
    },
    target: xpSynced
});
$xpServerBase.on(xpSynced, (_, { base }) => base);
$xpPendingDelta.on(xpSynced, (_, { delta }) => delta);
sample({
    clock: xpSynced,
    source: { total: $xpTotal, flights: $xpFlights },
    filter: ({ flights }) => flights.length === 0,
    fn: ({ total }) => total,
    target: $xpPillValue
});
sample({ clock: xpSynced, filter: ({ delta }) => delta > 0, target: xpPersistRequested });

$xpWriteInFlight.on(xpWriteStarted, () => true).on(xpWriteSettled, () => false);
$xpWriteRetries.on(xpWriteSettled, (retries, { failed }) => (failed ? retries + 1 : 0));
sample({
    clock: xpWriteSettled,
    source: $xpWriteRetries,
    filter: (retries, { failed }) => failed && retries < MAX_WRITE_RETRIES,
    target: xpPersistRequested
});

const contract = new ModelContract(
    { $xpEnabled, $xpTotal, $xpLevel, $xpProgress, $levelUp, $lastLevelUp, $lastLessonAward, $xpFlights, $xpPillValue },
    { xpAwardRequested, xpAwarded, xpFlightFinished, xpPillSynced, levelUpShown },
    { trackXpAwardedFx, trackLevelUpFx },
    { $persistDebounceMs, $xpServerBase, $xpPendingDelta, $xpWriteInFlight }
);

export type IXpContract = typeof contract;

export class XpModule extends ModuleDescription<IXpContract> {
    constructor(
        public metadataModule: IMetadataContract,
        public taskLayoutUIModule: ITaskLayoutUIContract,
        public tasksModule: ITasksContract,
        public lessonCompletionModule: ILessonCompletionContract,
        public userAuthModule: IUserAuthContract,
        public amplitudeFlagsModule: IAmplitudeFlagsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        const { metadataLoaded, setMetadataRequested } = this.metadataModule.events;
        const { setMetadataFx, deleteMetadataFx } = this.metadataModule.effects;

        // Mirrors the flag on every change and once on mount (a flag already resolved never "updates").
        sample({
            clock: [this.amplitudeFlagsModule.stores.$gamificationEnabled, appMounted],
            source: this.amplitudeFlagsModule.stores.$gamificationEnabled,
            fn: (flag) => flag === true,
            target: $xpEnabled
        });

        // Hook points
        sample({
            clock: this.taskLayoutUIModule.events.taskAnswerChecked,
            filter: (check) => check.isCorrect && !check.usesSavedAnswer && !check.wasAlreadyCorrect,
            fn: (check): XpAwardRequest => ({
                source: "task",
                amount: XP_REWARDS.task,
                key: `task:${check.taskId}`,
                taskId: check.taskId
            }),
            target: xpAwardRequested
        });

        sample({
            clock: this.tasksModule.effects.taskCheckedFx.done,
            filter: ({ result }) => result.status === "completed",
            fn: ({ params }): XpAwardRequest => ({
                source: "task",
                amount: XP_REWARDS.task,
                key: `quiz:${params.taskId}-${params.lessonId}`,
                taskId: `quiz-${params.taskId}`
            }),
            target: xpAwardRequested
        });

        sample({
            clock: this.lessonCompletionModule.events.lessonCompletionSettled,
            source: $repeatLog,
            filter: (log, result) => result.isFirstCompletion || !repeatPaidToday(log, result.lessonId),
            fn: (_, result): XpAwardRequest => {
                const award = xpForCompletion(result);
                return {
                    source: award.source,
                    amount: award.amount,
                    key: `lesson:${result.lessonId}:${award.source}`,
                    taskId: null,
                    breakdown: award.breakdown,
                    lessonId: result.lessonId
                };
            },
            target: xpAwardRequested
        });

        // The repeat log is written at once (it is small and only ever grows within a day).
        sample({
            clock: xpAwarded,
            source: $repeatLog,
            filter: (_, award) => award.source === "lesson_repeat" && award.lessonId !== null,
            // Computed from the award, not read back from the store: no dependence on update order.
            fn: (log, award) => ({
                key: XP_REPEAT_LOG_KEY,
                value: formatRepeatLog(withRepeatPaid(log, award.lessonId!))
            }),
            target: setMetadataRequested
        });

        // Every server map carries the keys: initial load, our own writes, other keys' writes, refetches.
        const serverMapReceived = [metadataLoaded, setMetadataFx.doneData, deleteMetadataFx.doneData];
        sample({
            clock: serverMapReceived,
            fn: (response) => parseServerXp(response.metadata),
            target: serverXpReceived
        });
        // A map without the key (a partial response) leaves the known log alone.
        sample({
            clock: serverMapReceived,
            filter: (response) => response.metadata[XP_REPEAT_LOG_KEY] !== undefined,
            fn: (response) => parseRepeatLog(response.metadata),
            target: repeatLogReceived
        });

        // Persistence: debounced, coalesced, one write in flight, flushed on route change.
        const persistDebounced = debounce({ source: xpPersistRequested, timeout: $persistDebounceMs });
        sample({ clock: routePathNameChanged, target: xpFlushRequested });

        const writeRequested = sample({
            clock: [persistDebounced, xpFlushRequested],
            source: { enabled: $xpEnabled, base: $xpServerBase, delta: $xpPendingDelta, inFlight: $xpWriteInFlight },
            filter: ({ enabled, base, delta, inFlight }) => enabled && base !== null && delta > 0 && !inFlight,
            fn: ({ base, delta }) => ({ key: XP_METADATA_KEY, value: String((base ?? 0) + delta) })
        });
        sample({ clock: writeRequested, target: xpWriteStarted });
        sample({ clock: writeRequested, target: setMetadataRequested });

        sample({
            clock: setMetadataFx.finally,
            filter: ({ params }) => params.key === XP_METADATA_KEY,
            fn: ({ status }) => ({ failed: status === "fail" }),
            target: xpWriteSettled
        });

        reset({
            clock: this.userAuthModule.events.logoutStudent,
            target: [
                $xpServerBase,
                $xpPendingDelta,
                $awardedKeys,
                $repeatLog,
                $levelUp,
                $lastLevelUp,
                $lastLessonAward,
                $xpFlights,
                $xpPillValue,
                $xpWriteInFlight,
                $xpWriteRetries
            ]
        });
    }
}
